import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { Gift, Eye, EyeOff, Phone, Lock, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoyaltyLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    whatsapp: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('loyalty_token');
    if (token) {
      navigate('/lealtad');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.whatsapp || !formData.password) {
      toast.error('Completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/loyalty/login`, {
        username: formData.whatsapp,
        password: formData.password,
      });
      
      toast.success(`¡Bienvenido, ${response.data.user.name}!`);
      localStorage.setItem('loyalty_token', response.data.token);
      navigate('/lealtad');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenciales incorrectas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-4" data-testid="loyalty-login">
      {/* Header */}
      <div className="text-center mb-8">
        <Gift className="w-12 h-12 text-gold mx-auto mb-4" />
        <h1 className="font-heading text-3xl text-white mb-2">Iniciar Sesión</h1>
        <p className="text-zinc-500">Accede a tu cuenta de lealtad</p>
      </div>

      {/* Form */}
      <Card className="w-full max-w-md bg-zinc-900/80 border-zinc-800 p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* WhatsApp */}
          <div className="space-y-2">
            <Label className="text-zinc-400 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              WhatsApp
            </Label>
            <Input
              value={formData.whatsapp}
              onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value.replace(/\D/g, '') })}
              placeholder="5512345678"
              className="bg-zinc-800 border-zinc-700 text-white h-12"
              data-testid="login-whatsapp-input"
            />
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label className="text-zinc-400 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Contraseña
            </Label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Tu contraseña"
                className="bg-zinc-800 border-zinc-700 text-white h-12 pr-12"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-14 bg-gold text-black hover:bg-gold-dark font-semibold uppercase tracking-wide"
            data-testid="login-submit-btn"
          >
            {loading ? 'Entrando...' : (
              <>
                Entrar
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-zinc-500 text-sm">
            ¿No tienes cuenta?{' '}
            <Link to="/lealtad/registro" className="text-gold hover:underline">
              Regístrate aquí
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
}
